
import React from "react";
import { useNavigate } from "react-router-dom";

import {
  FaBookOpen,
  FaLaptopCode,
  FaUsers,
  FaGlobe,
  FaRunning,
  FaLeaf,
  FaHandsHelping,
  FaFlask,
  FaBrain,
  FaGraduationCap,
  FaRobot,
  FaAward,
  FaArrowRight,
  FaCheckCircle,
  FaMicroscope,
  FaLightbulb,
  FaShieldAlt,
  FaChalkboardTeacher,
} from "react-icons/fa";

import "./HighSchool.css";

const highSchoolPrograms = [
  {
    icon: <FaFlask />,
    number: "01",
    title: "STEM & Scientific Discovery",
    description:
      "Explore science, technology, engineering and mathematics through practical activities, investigations and interdisciplinary learning.",
    color: "blue",
  },
  {
    icon: <FaRobot />,
    number: "02",
    title: "Robotics & Coding",
    description:
      "Introduce computational thinking, programming concepts and robotics challenges that encourage creativity and logical reasoning.",
    color: "purple",
  },
  {
    icon: <FaBrain />,
    number: "03",
    title: "Critical Thinking",
    description:
      "Strengthen analytical reasoning, independent research and problem-solving skills through thoughtful academic exploration.",
    color: "orange",
  },
  {
    icon: <FaGlobe />,
    number: "04",
    title: "Global Perspectives",
    description:
      "Encourage curiosity about different cultures, perspectives and global issues while developing communication skills.",
    color: "green",
  },
  {
    icon: <FaMicroscope />,
    number: "05",
    title: "Practical Learning",
    description:
      "Connect classroom theory with investigations, experiments, demonstrations and meaningful project-based activities.",
    color: "pink",
  },
  {
    icon: <FaLightbulb />,
    number: "06",
    title: "Innovation & Creativity",
    description:
      "Inspire students to develop original ideas, explore solutions and approach new challenges with confidence.",
    color: "yellow",
  },
];

const developmentAreas = [
  {
    icon: <FaGraduationCap />,
    title: "Academic Achievement",
    description:
      "Build subject knowledge, effective study habits and examination preparation skills for the next stage of education.",
  },
  {
    icon: <FaUsers />,
    title: "Leadership & Teamwork",
    description:
      "Develop collaboration, communication, accountability and the confidence to contribute positively to a team.",
  },
  {
    icon: <FaRunning />,
    title: "Sports & Wellbeing",
    description:
      "Encourage physical activity, sportsmanship, resilience and a healthy balance between academics and personal wellbeing.",
  },
  {
    icon: <FaHandsHelping />,
    title: "Character & Service",
    description:
      "Promote empathy, respect, integrity and responsible participation in school and community activities.",
  },
];

const HighSchool = () => {
  const navigate = useNavigate();

  return (
    <main className="ags-high">

      {/* HERO SECTION */}
      <section className="ags-high-hero">
        <div className="ags-high-container ags-high-hero-grid">

          <div className="ags-high-hero-copy">
            <span className="ags-high-eyebrow">
              ACADEMIC GROUP OF SCHOOLS & COLLEGE
            </span>

            <span className="ags-high-level">
              <FaGraduationCap />
              SECONDARY & COLLEGE EDUCATION · GRADES 9–12
            </span>

            <h1>
              Discover Your Potential.
              <span> Prepare for Your Future.</span>
            </h1>

            <p>
              The secondary school years are an important step towards
              academic achievement, personal growth and future career
              opportunities. At Academic Group of Schools (AGS), we
              encourage students to explore their interests, strengthen
              their knowledge and develop the skills to face tomorrow's
              challenges.
            </p>

            <div className="ags-high-hero-actions">
              <a href="#high-pathways" className="ags-high-btn">
                Explore Study Pathways <FaArrowRight />
              </a>

              <a href="#high-stem" className="ags-high-secondary-link">
                Discover STEM Learning
              </a>
            </div>

            <div className="ags-high-promise">
              <FaCheckCircle />
              <span>STEM · SKILLS · SERVICE</span>
            </div>
          </div>

          <div className="ags-high-hero-art">
            <div className="ags-high-orbit ags-high-orbit-one" />
            <div className="ags-high-orbit ags-high-orbit-two" />

            <div className="ags-high-art-panel">
              <div className="ags-high-art-icon">
                <FaGraduationCap />
              </div>

              <span>YOUR JOURNEY STARTS HERE</span>

              <h2>Learn.</h2>
              <h2>Innovate.</h2>
              <h2 className="ags-high-gold-text">Lead.</h2>

              <p>
                Knowledge, character and a world of possibilities.
              </p>

              <div className="ags-high-art-icons">
                <span><FaBookOpen /></span>
                <span><FaRobot /></span>
                <span><FaLaptopCode /></span>
              </div>
            </div>

            <div className="ags-high-float ags-high-float-top">
              <FaAward />
              <span>Strive for Excellence</span>
            </div>

            <div className="ags-high-float ags-high-float-bottom">
              <FaLightbulb />
              <span>Think Beyond Limits</span>
            </div>
          </div>

        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="ags-high-section ags-high-intro">
        <div className="ags-high-container ags-high-intro-grid">

          <div className="ags-high-intro-heading">
            <span className="ags-high-label">OUR EDUCATIONAL VISION</span>

            <h2>
              Building Strong Foundations
              <span> for Tomorrow's Success</span>
            </h2>

            <div className="ags-high-accent-line" />
          </div>

          <div className="ags-high-intro-copy">
            <p>
              At Academic Group of Schools & College, our educational
              vision is to prepare students for a rapidly changing world.
              Guided by our motto, <strong>STEM, SKILLS, SERVICE</strong>,
              we value academic progress alongside creativity,
              responsible decision-making and personal development.
            </p>

            <p>
              Through structured classroom learning, practical
              exploration and opportunities to collaborate, students
              can develop a deeper understanding of their subjects
              and prepare for future educational choices.
            </p>

            <p>
              We believe that education should encourage students
              to ask questions, explore ideas, respect others and
              become confident, thoughtful lifelong learners.
            </p>
          </div>

        </div>
      </section>

      {/* STEM SECTION */}
      <section className="ags-high-section ags-high-stem" id="high-stem">
        <div className="ags-high-container ags-high-stem-grid">

          <div className="ags-high-stem-copy">
            <span className="ags-high-label">LEARNING THROUGH INNOVATION</span>

            <h2>
              Explore STEM.
              <span> Shape What's Next.</span>
            </h2>

            <p>
              Science, Technology, Engineering and Mathematics help
              students understand how the world works. At AGS, STEM
              learning can connect academic concepts with practical
              exploration, creative design and problem-solving.
            </p>

            <div className="ags-high-stem-points">
              <div>
                <FaCheckCircle />
                <span>Scientific exploration and experimentation</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Computational thinking and coding concepts</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Engineering challenges and creative projects</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Analytical reasoning and practical applications</span>
              </div>
            </div>
          </div>

          <div className="ags-high-stem-visual">
            <div className="ags-high-stem-circle ags-high-stem-circle-one" />
            <div className="ags-high-stem-circle ags-high-stem-circle-two" />

            <div className="ags-high-stem-center">
              <FaLaptopCode />
              <span>STEM</span>
              <strong>Explore. Create. Solve.</strong>
            </div>

            <div className="ags-high-stem-bubble ags-high-bubble-one">
              <FaFlask />
              <span>Science</span>
            </div>

            <div className="ags-high-stem-bubble ags-high-bubble-two">
              <FaLaptopCode />
              <span>Technology</span>
            </div>

            <div className="ags-high-stem-bubble ags-high-bubble-three">
              <FaRobot />
              <span>Engineering</span>
            </div>

            <div className="ags-high-stem-bubble ags-high-bubble-four">
              <FaBrain />
              <span>Mathematics</span>
            </div>
          </div>

        </div>
      </section>

      {/* LEARNING PROGRAMS */}
      <section className="ags-high-section ags-high-programs">
        <div className="ags-high-container">

          <div className="ags-high-heading">
            <span className="ags-high-label">A WORLD OF DISCOVERY</span>

            <h2>
              Learning Beyond
              <span> the Textbook</span>
            </h2>

            <p>
              A broad educational experience that encourages students
              to think independently, develop practical skills and
              discover new interests.
            </p>
          </div>

          <div className="ags-high-program-grid">
            {highSchoolPrograms.map((program) => (
              <article
                className={`ags-high-program-card ags-high-${program.color}`}
                key={program.number}
              >
                <div className="ags-high-card-top">
                  <div className="ags-high-card-icon">
                    {program.icon}
                  </div>

                  <span>{program.number}</span>
                </div>

                <h3>{program.title}</h3>

                <p>{program.description}</p>

                <div className="ags-high-card-footer">
                  <span>Build your potential</span>
                  <FaArrowRight />
                </div>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* STUDY PATHWAYS */}
      <section
        className="ags-high-section ags-high-pathways"
        id="high-pathways"
      >
        <div className="ags-high-container">

          <div className="ags-high-heading">
            <span className="ags-high-label">ACADEMIC PATHWAYS</span>

            <h2>
              Choosing Your
              <span> Academic Pathway</span>
            </h2>

            <p>
              Different curricula offer different learning experiences.
              Students and families can explore the pathway that best
              aligns with their educational plans and future goals.
            </p>
          </div>

          <div className="ags-high-pathway-grid">

            <article className="ags-high-pathway-card">
              <div className="ags-high-pathway-icon">
                <FaBookOpen />
              </div>

              <span className="ags-high-pathway-tag">
                LOCAL CURRICULUM
              </span>

              <h3>Matriculation</h3>

              <p>
                A pathway aligned with Pakistan's local secondary
                education framework, with an emphasis on subject
                knowledge, examination preparation and academic
                progression.
              </p>

              <div className="ags-high-pathway-bottom">
                <FaCheckCircle />
                <span>Foundation for further local studies</span>
              </div>
            </article>

            <article className="ags-high-pathway-card ags-high-pathway-featured">
              <div className="ags-high-pathway-icon">
                <FaGlobe />
              </div>

              <span className="ags-high-pathway-tag">
                CAMBRIDGE CURRICULUM
              </span>

              <h3>Cambridge O Level</h3>

              <p>
                An international curriculum pathway that emphasizes
                subject understanding, application of knowledge,
                independent learning and preparation for further
                academic opportunities.
              </p>

              <div className="ags-high-pathway-bottom">
                <FaCheckCircle />
                <span>Internationally oriented academic pathway</span>
              </div>
            </article>

          </div>

          <p className="ags-high-pathway-note">
            Please contact the school to confirm currently offered
            curricula, available subjects, examination boards and
            admission requirements.
          </p>

        </div>
      </section>

      {/* INTERMEDIATE & COLLEGE PROGRAMS */}
      <section
        className="ags-high-section ags-high-college"
        id="intermediate-programs"
      >
        <div className="ags-high-container">
          <div className="ags-high-heading ags-high-college-heading">
            <span className="ags-high-label">YOUR NEXT ACADEMIC CHAPTER</span>
            <h2>
              Welcome to <span>College-Level Learning</span>
            </h2>
            <p>
              The AGS learning journey can continue beyond Grades 9–10.
              Our Intermediate-level pathway is designed for students
              progressing into 11th and 12th grade, with a focus on
              subject knowledge, independent learning and preparation
              for higher education.
            </p>
          </div>

          <div className="ags-high-college-grid">
            <article className="ags-high-college-card">
              <div className="ags-high-college-icon">
                <FaGraduationCap />
              </div>
              <span className="ags-high-college-tag">Grades 11–12</span>
              <h3>Intermediate Education</h3>
              <p>
                A college-level academic stage that helps students deepen
                their subject knowledge, strengthen study skills and
                prepare for further education and future career choices.
              </p>
              <ul>
                <li>Focused subject learning</li>
                <li>Academic development and exam preparation</li>
                <li>Preparation for university pathways</li>
              </ul>
            </article>

            <article className="ags-high-college-card ags-high-college-featured">
              <div className="ags-high-college-icon">
                <FaLaptopCode />
              </div>
              <span className="ags-high-college-tag">Computer Science</span>
              <h3>ICS — Intermediate in Computer Science</h3>
              <p>
                A pathway for students interested in computing and
                technology, supporting the development of logical thinking
                and foundations for further study in computer science,
                information technology and related fields.
              </p>
              <ul>
                <li>Computer science foundations</li>
                <li>Logical and analytical thinking</li>
                <li>Preparation for technology-related degrees</li>
              </ul>
            </article>

            <article className="ags-high-college-card">
              <div className="ags-high-college-icon">
                <FaChalkboardTeacher />
              </div>
              <span className="ags-high-college-tag">Future Ready</span>
              <h3>University & Career Preparation</h3>
              <p>
                Students are encouraged to build confidence, manage their
                learning independently and make informed decisions about
                their next academic steps.
              </p>
              <ul>
                <li>Independent learning skills</li>
                <li>Academic goals and planning</li>
                <li>Readiness for higher education</li>
              </ul>
            </article>
          </div>

          <div className="ags-high-college-note">
            <div className="ags-high-college-note-icon">
              <FaBookOpen />
            </div>
            <div>
              <h3>From School to College, With Confidence</h3>
              <p>
                From secondary studies to Grades 11–12, AGS aims to help
                students move forward with knowledge, responsibility and
                a clear sense of purpose.
              </p>
            </div>
          </div>

          <p className="ags-high-pathway-note">
            Please confirm with the campus which Intermediate groups,
            subjects, examination board and admission options are currently
            available.
          </p>
        </div>
      </section>

      {/* STUDENT DEVELOPMENT */}
      <section className="ags-high-section ags-high-development">
        <div className="ags-high-container ags-high-development-grid">

          <div className="ags-high-development-copy">
            <span className="ags-high-label">GROWING EVERY DAY</span>

            <h2>
              More Than Academics.
              <span> A Complete Learning Journey.</span>
            </h2>

            <p>
              Secondary education is also a time to discover personal
              strengths, build healthy relationships and develop
              the confidence to take responsibility. Our educational
              vision encourages students to grow academically,
              socially and personally.
            </p>

            <div className="ags-high-development-highlight">
              <FaGraduationCap />

              <div>
                <strong>Preparing for the Next Chapter</strong>
                <p>
                  Supporting students as they work towards higher
                  education, future careers and responsible citizenship.
                </p>
              </div>
            </div>
          </div>

          <div className="ags-high-development-list">
            {developmentAreas.map((area, index) => (
              <article className="ags-high-development-item" key={area.title}>
                <div className="ags-high-development-icon">
                  {area.icon}
                </div>

                <div>
                  <span>
                    AREA {String(index + 1).padStart(2, "0")}
                  </span>

                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </div>
              </article>
            ))}
          </div>

        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="ags-high-section ags-high-activities">
        <div className="ags-high-container ags-high-activities-grid">

          <div className="ags-high-activities-heading">
            <span className="ags-high-label">LEARN. PARTICIPATE. CONTRIBUTE.</span>

            <h2>
              Building Character
              <span> Beyond the Classroom</span>
            </h2>

            <p>
              Learning opportunities outside regular lessons can
              help students develop teamwork, creativity, confidence
              and a sense of responsibility.
            </p>
          </div>

          <div className="ags-high-activity-cards">

            <article className="ags-high-activity-card">
              <div className="ags-high-activity-icon">
                <FaRunning />
              </div>

              <div>
                <h3>Sports & Activities</h3>
                <p>
                  Encourage participation, teamwork, perseverance
                  and sportsmanship through appropriate physical
                  and extracurricular activities.
                </p>
              </div>
            </article>

            <article className="ags-high-activity-card">
              <div className="ags-high-activity-icon ags-high-activity-green">
                <FaLeaf />
              </div>

              <div>
                <h3>Environmental Awareness</h3>
                <p>
                  Develop awareness of sustainability, responsible
                  resource use and the importance of caring for
                  the environment.
                </p>
              </div>
            </article>

            <article className="ags-high-activity-card">
              <div className="ags-high-activity-icon ags-high-activity-gold">
                <FaHandsHelping />
              </div>

              <div>
                <h3>Community Service</h3>
                <p>
                  Promote empathy and civic responsibility through
                  opportunities to contribute to community and
                  school initiatives.
                </p>
              </div>
            </article>

            <article className="ags-high-activity-card">
              <div className="ags-high-activity-icon ags-high-activity-purple">
                <FaShieldAlt />
              </div>

              <div>
                <h3>Respect & Wellbeing</h3>
                <p>
                  Encourage respectful relationships, inclusion,
                  positive communication and a supportive learning
                  environment.
                </p>
              </div>
            </article>

          </div>

        </div>
        
      </section>
      

      {/* CLOSING */}
      <section className="ags-high-closing">
        <div className="ags-high-container ags-high-closing-content">

          <div className="ags-high-closing-icon">
            <FaGraduationCap />
          </div>

          <span>ACADEMIC GROUP OF SCHOOLS & COLLEGE</span>

          <h2>
            Inspiring Excellence.
            <br />
            Empowering Tomorrow.
          </h2>

          <p>
            Every student has the potential to achieve great things.
            Through knowledge, innovation, character and service,
            we aim to inspire young people to take their next steps
            with confidence and purpose.
          </p>

          {/* <a href="#high-pathways" className="ags-high-btn">
            Explore Academic Pathways <FaArrowRight />
          </a> */}
          <button className="ags-high-btn" onClick={() => navigate("/ApplyForm")}>
            Apply Now <FaArrowRight />
          </button>

        </div>
        
      </section>
      

    </main>
    
  );
  
};

export default HighSchool;
